import { useCallback } from "react";
import { setUserData } from "../store/user";

import {
  AnalyticEvent,
  phoneWithCountryCode,
  useCreateCustomerProfileMutation,
  useGetUserProfileLazyQuery,
  usePostHog,
} from "@stepapp/core";
import type { RegisterTypeEnum } from "@stepapp/core/apiTypes";
import { addSentryBreadcrumb, captureSentryException } from "@stepapp/sentry";

import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { loaderRef } from "../components/Loader";
import { AppStorage } from "../services/AppStorage";
import { useAppDispatch } from "./redux";

type Params = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  registerType: RegisterTypeEnum;
};

const useSignUp = () => {
  const { t } = useTranslation();
  const postHog = usePostHog();
  const appDispatch = useAppDispatch();
  const [createCustomerProfile] = useCreateCustomerProfileMutation();
  const [getUserProfile] = useGetUserProfileLazyQuery({
    variables: {
      isCustomerProfile: true,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const showAlert = (error?: string) => {
    const emailAlreadyExists = error?.includes("email already exists");
    const emailIsWrong = error?.includes("Email is incorrect");

    if (emailAlreadyExists) {
      return alert(t("alerts.userWithEmailAlreadyExists"));
    }
    if (emailIsWrong) {
      return alert(t("alerts.emailIsIncorrect"));
    }
    return alert(t("alerts.cantCreateUser"));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleSignUp = useCallback(
    async ({ email, firstName, lastName, phoneNumber, registerType }: Params) => {
      loaderRef.current?.showLoader();

      const { data, errors } = await createCustomerProfile({
        variables: {
          data: {
            email,
            firstName,
            lastName,
            registerType,
            phone: phoneWithCountryCode(phoneNumber, AppStorage().get("countryCode")),
          },
        },
      });

      if (data?.createCustomerProfile) {
        const { data: getUserData } = await getUserProfile();
        if (getUserData?.getMyProfile) {
          appDispatch(setUserData(getUserData.getMyProfile));
          const {
            id,
            profileInformation: { firstName, lastName, email, phone },
          } = getUserData.getMyProfile;

          postHog?.identify(id, {
            platform: Platform.OS,
            name: `${firstName} ${lastName}`,
            email,
            phoneNumber: phone,
          });
          postHog?.capture(AnalyticEvent.Register.Success);
          postHog?.capture(AnalyticEvent.GTM.SignUp, { ecommerce: null });
        }
        loaderRef.current?.hideLoader();

        return true;
      }

      addSentryBreadcrumb("[Register error]", { level: "error", category: "network", data });
      captureSentryException("[Register error]", {
        extra: {
          errorMessage: errors?.[0]?.message,
        },
      });
      showAlert(errors?.[0]?.message);
      loaderRef.current?.hideLoader();
      return false;
    },
    [createCustomerProfile, getUserProfile, postHog, appDispatch],
  );

  return handleSignUp;
};

export default useSignUp;
